import { apiClient } from './api.client';
import type { ClientsResponse, ClientFilters } from '@lnr-v2/types';
import { ALLOWED_INDUSTRIES } from '../constants';

/**
 * Parameters for fetching clients
 *
 * All fields are optional to support flexible querying.
 * The backend supports pagination, sorting, and filtering.
 */
export interface FetchClientsParams {
  /** Filter by tags - comma-separated string (e.g., "Travel,Tech") */
  tags?: string;
  /** Search query string */
  search?: string;
  /** Filter by active status */
  isActive?: boolean;
  /** Filter by featured status */
  isFeatured?: boolean;
  /** Filter by industry */
  industry?: string;
  /** Sort field */
  sortBy?: 'name' | 'createdAt' | 'updatedAt';
  /** Sort direction */
  sortOrder?: 'ASC' | 'DESC';
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
}

/**
 * Result from fetching clients
 *
 * Matches the API response structure for type safety.
 */
export interface FetchClientsResult extends ClientsResponse {}

/**
 * Response type for taxonomy endpoint
 */
interface TaxonomyResponse {
  success: boolean;
  data: {
    id: string;
    name: string;
    slug: string;
    terms: Array<{
      id: string;
      nameEN: string;
      nameFR: string;
      slug: string;
    }>;
  };
}

/**
 * Industry type from the API
 */
export interface Industry {
  id: string;
  name: string;
  slug: string;
}

/**
 * Service layer for client-related API operations
 *
 * Provides a clean interface for fetching client data with proper
 * type safety and separation from React components.
 */
export const clientsService = {
  /**
   * Fetch clients from the API with optional filtering and pagination
   *
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise resolving to ClientsResponse with items and pagination
   *
   * @example
   * ```ts
   * const result = await clientsService.fetchClients({
   *   tags: 'Travel,Tech',
   *   page: 1,
   *   limit: 20
   * });
   * ```
   */
  async fetchClients(
    params: FetchClientsParams = {}
  ): Promise<FetchClientsResult> {
    // Prepare query parameters, excluding undefined values
    const queryParams: Partial<ClientFilters> = {};

    // Handle tags filter - don't send "All" to the API
    if (params.tags && params.tags !== 'All') {
      queryParams.tags = params.tags;
    }

    if (params.search !== undefined) queryParams.search = params.search;
    if (params.isActive !== undefined) queryParams.isActive = params.isActive;
    if (params.isFeatured !== undefined)
      queryParams.isFeatured = params.isFeatured;
    if (params.industry !== undefined) queryParams.industry = params.industry;
    if (params.sortBy !== undefined) queryParams.sortBy = params.sortBy;
    if (params.sortOrder !== undefined)
      queryParams.sortOrder = params.sortOrder;
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.limit !== undefined) queryParams.limit = params.limit;

    const response = await apiClient.get<ClientsResponse>('/clients', {
      params: queryParams,
    });

    return response.data;
  },

  /**
   * Fetch industries from the taxonomy API
   *
   * Retrieves available industries dynamically from the Industries taxonomy.
   * Filters to only return allowed industries for the web UI.
   *
   * @returns Promise resolving to array of Industry objects
   */
  async fetchIndustries(): Promise<Industry[]> {
    const response = await apiClient.get<TaxonomyResponse>(
      '/taxonomies/industries',
      {
        params: { includeTerms: true },
      }
    );

    // Map API response to Industry format (use English name as default)
    const terms = response.data.data.terms || [];
    const allIndustries = terms.map((term) => ({
      id: term.id,
      name: term.nameEN,
      slug: term.slug,
    }));

    // Filter to only show allowed industries (from shared constant)
    return allIndustries.filter((industry) =>
      ALLOWED_INDUSTRIES.includes(
        industry.name as (typeof ALLOWED_INDUSTRIES)[number]
      )
    );
  },
};
